import React from 'react';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import ContainedButton from '../../../components/base/ContainedButton';
import { useMenu } from '../../../components/base/useMenu';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import TextButton from '../../../components/base/TextButton';
import DescriptionIcon from '@mui/icons-material/Description';
import { useTheme } from '@emotion/react';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Fade from '@mui/material/Fade';
import Zoom from '@mui/material/Zoom';
import Collapse from '@mui/material/Collapse';
import SettingsIcon from '@mui/icons-material/Settings';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import TableChartIcon from '@mui/icons-material/TableChart';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';

const TestCaseReports = ({
  onExportTestCase,
  isExportTestCases,
  setFormat,
  format,
  disabled,
  mode,
  setMode
}) => {
  const { menuProps, triggerProps } = useMenu();
  const theme = useTheme();

  const formatOptions = [
    {
      value: 'excel',
      label: 'Excel',
      description: 'Rich formatting & multiple sheets',
      icon: <TableChartIcon fontSize="small" />,
      color: theme.palette.success.main
    },
    {
      value: 'csv',
      label: 'CSV',
      description: 'Plain text, universally compatible',
      icon: <DescriptionIcon fontSize="small" />,
      color: theme.palette.info.main
    }
  ];

  const modeOptions = [
    {
      value: 'simple',
      label: 'Simple',
      description: 'Basic test case information, cleaner output',
      icon: <AutoAwesomeIcon fontSize="small" />,
      features: ['ID', 'Title', 'Status', 'Priority', 'Assigned To']
    },
    {
      value: 'advanced',
      label: 'Advanced',
      description: 'Complete test data with steps & history',
      icon: <SettingsIcon fontSize="small" />,
      features: ['All simple fields', 'Test steps', 'Comments']
    }
  ];

  return (
    <Box>
      <Tooltip
        title={disabled ? 'Select test cases to export' : 'Export test cases'}
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: theme.palette.grey[800],
              color: theme.palette.common.white,
              fontSize: '0.75rem',
              fontWeight: 400,
              '& .MuiTooltip-arrow': {
                color: theme.palette.grey[800]
              }
            }
          }
        }}
        arrow
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 200 }}>
        <span>
          <TextButton
            {...triggerProps}
            loadingProps={{ size: 16 }}
            disabled={disabled}
            startIcon={
              <CloudDownloadIcon
                sx={{
                  color: disabled ? theme.palette.action.disabled : theme.palette.primary.main,
                  transition: 'transform 0.2s ease',
                  ...(!disabled && {
                    '&:hover': {
                      transform: 'translateY(-2px)'
                    }
                  })
                }}
              />
            }
            isLoading={isExportTestCases}
            sx={{
              position: 'relative',
              '&:hover:not(.Mui-disabled)': {
                backgroundColor: theme.palette.action.hover,
                '& .MuiSvgIcon-root': {
                  transform: 'translateY(-2px)'
                }
              },
              '&.Mui-disabled': {
                backgroundColor: 'transparent'
              },
              transition: 'all 0.2s ease'
            }}>
            Export
          </TextButton>
        </span>
      </Tooltip>

      <Popover
        {...menuProps}
        elevation={8}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        TransitionComponent={Fade}
        TransitionProps={{ timeout: 250 }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            minWidth: 380,
            maxWidth: 440,
            overflow: 'visible',
            backgroundImage: 'none',
            backgroundColor: theme.palette.background.paper,
            boxShadow:
              theme.palette.mode === 'dark'
                ? '0 10px 30px rgba(0, 0, 0, 0.5)'
                : '0 10px 30px rgba(0, 0, 0, 0.1)',
            '& .MuiPopover-paper': {
              overflow: 'visible'
            }
          }
        }}>
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '6px',
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            borderTopLeftRadius: 12,
            borderTopRightRadius: 12
          }}
        />

        <Box
          sx={{
            px: 3,
            py: 3,
            position: 'relative',
            zIndex: 1,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 3
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 1.5 }}>
            <Paper
              elevation={0}
              sx={{
                p: 1,
                borderRadius: 2,
                backgroundColor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(144, 202, 249, 0.12)'
                    : 'rgba(33, 150, 243, 0.08)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
              <DescriptionIcon
                fontSize="small"
                sx={{
                  color: theme.palette.primary.main
                }}
              />
            </Paper>
            <Box>
              <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                Export Test Cases
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Choose format and customize your export
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Box sx={{ mb: 3.5 }}>
            <FormControl fullWidth>
              <FormLabel
                sx={{
                  mb: 2,
                  color: theme.palette.text.primary,
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  '&.Mui-focused': {
                    color: theme.palette.primary.main
                  }
                }}>
                <TableChartIcon fontSize="small" sx={{ opacity: 0.7 }} />
                Export Format
              </FormLabel>
              <RadioGroup
                value={format}
                onChange={(e) => {
                  setFormat(e.target.value);
                  // Reset mode to 'simple' when switching to CSV (optional behavior)
                  if (e.target.value === 'csv' && mode === 'advanced') {
                    setMode?.('simple');
                  }
                }}
                sx={{
                  gap: 1.5,
                  '& .MuiFormControlLabel-root': {
                    margin: 0
                  }
                }}>
                {formatOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio sx={{ display: 'none' }} />}
                    label={
                      <Paper
                        elevation={0}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          width: '100%',
                          p: 1.5,
                          border: `2px solid ${
                            format === option.value
                              ? theme.palette.primary.main
                              : theme.palette.divider
                          }`,
                          borderRadius: 2,
                          backgroundColor:
                            format === option.value
                              ? theme.palette.mode === 'dark'
                                ? 'rgba(144, 202, 249, 0.08)'
                                : 'rgba(33, 150, 243, 0.04)'
                              : 'transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            borderColor:
                              format === option.value
                                ? theme.palette.primary.main
                                : theme.palette.primary.light,
                            transform: 'translateY(-2px)',
                            boxShadow: theme.shadows[2]
                          }
                        }}
                        onClick={() => setFormat(option.value)}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            backgroundColor: option.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: format === option.value ? 1 : 0.7,
                            transition: 'opacity 0.2s ease'
                          }}>
                          {React.cloneElement(option.icon, {
                            sx: { color: 'white' }
                          })}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" fontWeight={600} color="text.primary">
                              {option.label}
                            </Typography>
                            {format === option.value && (
                              <Zoom in={format === option.value}>
                                <CheckCircleIcon
                                  fontSize="small"
                                  sx={{
                                    color: theme.palette.success.main,
                                    fontSize: '1rem'
                                  }}
                                />
                              </Zoom>
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block', mt: 0.25 }}>
                            {option.description}
                          </Typography>
                        </Box>
                      </Paper>
                    }
                    sx={{ m: 0 }}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>

          <Collapse in={format === 'excel' && !!setMode}>
            <Box sx={{ mb: 3.5 }}>
              <FormControl fullWidth>
                <FormLabel
                  sx={{
                    mb: 2,
                    color: theme.palette.text.primary,
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    '&.Mui-focused': {
                      color: theme.palette.primary.main
                    }
                  }}>
                  <SettingsIcon fontSize="small" sx={{ opacity: 0.7 }} />
                  Export Mode
                </FormLabel>
                <RadioGroup
                  value={mode}
                  onChange={(e) => setMode(e.target.value)}
                  sx={{
                    gap: 1.5,
                    '& .MuiFormControlLabel-root': {
                      margin: 0
                    }
                  }}>
                  {modeOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      control={<Radio sx={{ display: 'none' }} />}
                      label={
                        <Paper
                          elevation={0}
                          sx={{
                            width: '100%',
                            p: 1.5,
                            border: `2px solid ${
                              mode === option.value
                                ? theme.palette.primary.main
                                : theme.palette.divider
                            }`,
                            borderRadius: 2,
                            backgroundColor:
                              mode === option.value
                                ? theme.palette.mode === 'dark'
                                  ? 'rgba(144, 202, 249, 0.08)'
                                  : 'rgba(33, 150, 243, 0.04)'
                                : 'transparent',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              borderColor:
                                mode === option.value
                                  ? theme.palette.primary.main
                                  : theme.palette.primary.light,
                              transform: 'translateY(-2px)',
                              boxShadow: theme.shadows[1]
                            }
                          }}
                          onClick={() => setMode(option.value)}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Box
                              sx={{
                                p: 0.75,
                                borderRadius: 1.5,
                                backgroundColor:
                                  mode === option.value
                                    ? theme.palette.primary.main
                                    : theme.palette.mode === 'dark'
                                    ? 'rgba(255, 255, 255, 0.05)'
                                    : 'rgba(0, 0, 0, 0.02)',
                                color:
                                  mode === option.value ? 'white' : theme.palette.text.secondary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                              {option.icon}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                <Typography variant="body2" fontWeight={600} color="text.primary">
                                  {option.label}
                                </Typography>
                                {mode === option.value && (
                                  <Zoom in={mode === option.value}>
                                    <CheckCircleIcon
                                      fontSize="small"
                                      sx={{
                                        color: theme.palette.success.main,
                                        fontSize: '1rem'
                                      }}
                                    />
                                  </Zoom>
                                )}
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 1 }}>
                                {option.description}
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {option.features.map((feature, idx) => (
                                  <Chip
                                    key={idx}
                                    label={feature}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.625rem',
                                      fontWeight: 500,
                                      borderColor: theme.palette.divider,
                                      backgroundColor:
                                        mode === option.value
                                          ? theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.02)'
                                            : 'rgba(0, 0, 0, 0.02)'
                                          : 'transparent'
                                    }}
                                  />
                                ))}
                              </Box>
                            </Box>
                          </Box>
                        </Paper>
                      }
                      sx={{ m: 0 }}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Box>
          </Collapse>

          <ContainedButton
            fullWidth
            size="large"
            onClick={() => {
              onExportTestCase();
              menuProps.onClose();
            }}
            startIcon={<CloudDownloadIcon />}
            disabled={disabled}
            loading={isExportTestCases}
            sx={{
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.dark} 90%)`,
              boxShadow: `0 4px 12px ${
                theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(33, 150, 243, 0.3)'
              }`,
              '&:hover:not(.Mui-disabled)': {
                background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                boxShadow: `0 6px 16px ${
                  theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(33, 150, 243, 0.4)'
                }`,
                transform: 'translateY(-2px)'
              },
              '&:active:not(.Mui-disabled)': {
                transform: 'translateY(0)',
                boxShadow: `0 2px 8px ${
                  theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.5)' : 'rgba(33, 150, 243, 0.3)'
                }`
              },
              '&.Mui-disabled': {
                background:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.12)'
                    : 'rgba(0, 0, 0, 0.12)',
                color: theme.palette.action.disabled,
                boxShadow: 'none'
              },
              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
            {isExportTestCases
              ? 'Preparing export...'
              : format === 'excel'
              ? `Export Excel (${mode === 'advanced' ? 'Advanced' : 'Simple'})`
              : 'Export CSV'}
          </ContainedButton>
        </Box>
      </Popover>
    </Box>
  );
};

export default TestCaseReports;
